import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import {
  AUTHORITY_TYPE,
  GENDER,
  MILTIS_SOCIAL_REGIMES,
  NIR_INHERITANCE_FROM,
} from "utils/constants";
import {
  bindInputProps,
  bindWebTextFieldInputProps,
} from "utils/helpers/input";
import {
  childNirIsManualEntry,
  childRequiresOffContractSpouseNir,
  getNirInheritanceFromStored,
  resolveChildInheritedNir,
} from "utils/helpers/miltisRelatedPersonChild";
import ActionButtons from "../Components/ActionButtons";
import DataProtection from "../Components/DataProtection";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import validation from "./validation";

const NUMBERS_MAPPING = {
  1: "1er",
  2: "2ème",
  3: "3ème",
  4: "4ème",
  5: "5ème",
  6: "6ème",
  7: "7ème",
  8: "8ème",
  9: "9ème",
  10: "10ème",
  11: "11ème",
  12: "12ème",
};

const isPendingParentNir = ({
  authority,
  nirInheritanceFrom,
  adherentNir,
  spouseNir,
  hasSpouse,
}) => {
  if (childNirIsManualEntry(authority, hasSpouse, nirInheritanceFrom)) {
    return false;
  }
  const adherent = (adherentNir || "").trim();
  const spouse = (spouseNir || "").trim();
  if (authority === AUTHORITY_TYPE.FATHER) return !adherent;
  if (authority === AUTHORITY_TYPE.MOTHER) return hasSpouse && !spouse;
  if (authority === AUTHORITY_TYPE.BOTH) {
    if (nirInheritanceFrom === NIR_INHERITANCE_FROM.SPOUSE)
      return !hasSpouse || !spouse;
    return !adherent;
  }
  return false;
};

function View({
  children,
  spouse,
  adherentSocialSecurityNumber = "",
  adherentSocialRegime = "",
  onNext,
  onBack,
  territorial = false,
}) {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const SOCIAL_REGIMES_TRANSLATION = t("B2C_MILTIS.SOCIAL_REGIMES", {
    returnObjects: true,
  });
  const { submitForm, values, setFieldValue, ...formProps } = useFormik({
    initialValues: { children, spouse },
    onSubmit: onNext,
    enableReinitialize: true,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      {
        hasPartner: !isEmpty(spouse),
        adherentSocialRegime,
      },
    ),
  });

  const hasSpouse = !isEmpty(spouse);

  const spouseNirSynced = get(values, "spouse.identity.socialSecurityNumber");
  const childrenAuthorityKey = (values.children || [])
    .map((c) => `${c?.authority ?? ""}|${getNirInheritanceFromStored(c) ?? ""}`)
    .join(";");

  useEffect(() => {
    const spouseNir = spouseNirSynced;
    (values.children || []).forEach((child, i) => {
      if (child.authority == null) return;
      const nirInheritanceFrom = getNirInheritanceFromStored(child);
      if (childNirIsManualEntry(child.authority, hasSpouse, nirInheritanceFrom)) {
        if (
          childRequiresOffContractSpouseNir(
            child.authority,
            hasSpouse,
            nirInheritanceFrom,
          )
        ) {
          const cur =
            get(values, `children.${i}.identity.socialSecurityNumber`) ?? "";
          const adherent = (adherentSocialSecurityNumber || "").trim();
          if (cur && cur === adherent) {
            setFieldValue(
              `children.${i}.identity.socialSecurityNumber`,
              "",
            );
          }
        }
        return;
      }
      const next = resolveChildInheritedNir({
        authority: child.authority,
        nirInheritanceFrom,
        adherentNir: adherentSocialSecurityNumber,
        spouseNir,
        hasSpouse,
      });
      const cur =
        get(values, `children.${i}.identity.socialSecurityNumber`) ?? "";
      const normalizedNext = next ?? "";
      if (cur !== normalizedNext) {
        setFieldValue(
          `children.${i}.identity.socialSecurityNumber`,
          normalizedNext,
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit full `values` to avoid sync loops
  }, [
    adherentSocialSecurityNumber,
    spouseNirSynced,
    childrenAuthorityKey,
    hasSpouse,
    setFieldValue,
  ]);

  const pendingParentLabel = (authority, nirInheritanceFrom) => {
    if (authority === AUTHORITY_TYPE.FATHER) {
      return t("B2C_MILTIS.RELATED_PERSONS.PARENT_NAME_ADHERENT");
    }
    if (authority === AUTHORITY_TYPE.MOTHER) {
      return t("B2C_MILTIS.RELATED_PERSONS.PARENT_NAME_SPOUSE");
    }
    if (authority === AUTHORITY_TYPE.BOTH) {
      return nirInheritanceFrom === NIR_INHERITANCE_FROM.SPOUSE
        ? t("B2C_MILTIS.RELATED_PERSONS.PARENT_NAME_SPOUSE")
        : t("B2C_MILTIS.RELATED_PERSONS.PARENT_NAME_ADHERENT");
    }
    return "";
  };

  return (
    <Layout>
      <Header
        title={`Nous avons besoin <span style="color: #4AA1F5">d'informations complémentaires</span> concernant <span style="color: #4AA1F5">vos bénéficiaires</span>`}
        subtitle="Vos bénéficiaires"
        onBack={onBack}
      />
      <div className="miltis-related-persons">
        <div className="miltis-related-persons__wrapper">
          {!isEmpty(spouse) && (
            <>
              <div className="miltis-related-persons__section">
                <h3 className="miltis-related-persons__section-title">
                  Informations de votre conjoint
                </h3>
                <div className="miltis-form">
                  <div className="miltis-form__container">
                    <Input
                      row
                      size="full"
                      label={"Civilité"}
                      type="radio"
                      values={[
                        {
                          value: GENDER.MALE,
                          label: "Homme",
                        },
                        {
                          value: GENDER.FEMALE,
                          label: "Femme",
                        },
                      ]}
                      {...bindInputProps({
                        name: "spouse.identity.user.gender",
                        values,
                        ...formProps,
                      })}
                      onChange={(value) =>
                        setFieldValue("spouse.identity.user.gender", value)
                      }
                    />
                    <div className="miltis-form__row">
                      <Input
                        placeholder={"ex : Doe"}
                        label={"Nom"}
                        {...bindWebTextFieldInputProps({
                          name: "spouse.identity.user.lastname",
                          values,
                          ...formProps,
                        })}
                      />

                      <Input
                        placeholder={"ex : John"}
                        label={"Prénom"}
                        {...bindWebTextFieldInputProps({
                          name: "spouse.identity.user.firstname",
                          values,
                          ...formProps,
                        })}
                      />
                    </div>
                    <div className="miltis-form__row">
                      <Input
                        placeholder={"ex : 185057800608436"}
                        label={"N° de Sécurité sociale"}
                        trim={true}
                        {...bindInputProps({
                          name: "spouse.identity.socialSecurityNumber",
                          values,
                          ...formProps,
                        })}
                      />

                      <Select
                        options={Object.values(MILTIS_SOCIAL_REGIMES)
                          .filter(
                            (option) =>
                              !territorial ||
                              option !==
                                MILTIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS,
                          )
                          .map((option) => {
                            const key = Object.keys(MILTIS_SOCIAL_REGIMES).find(
                              (k) => MILTIS_SOCIAL_REGIMES[k] === option,
                            );
                            return {
                              value: option,
                              label: SOCIAL_REGIMES_TRANSLATION[key] || option,
                            };
                          })}
                        placeholder="Sélectionnez un régime"
                        label="Régime social"
                        value={get(values, "spouse.identity.socialRegime")}
                        onChange={(value) =>
                          setFieldValue("spouse.identity.socialRegime", value)
                        }
                        errors={get(
                          formProps.errors,
                          "spouse.identity.socialRegime",
                          "",
                        )}
                        touched={get(
                          formProps.touched,
                          "spouse.identity.socialRegime",
                          false,
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="miltis-related-persons__divider" />
            </>
          )}
          {children.map((_, i) => {
            const authority = get(values, `children.${i}.authority`);
            const childRow = get(values, `children.${i}`);
            const nirInheritanceFrom = getNirInheritanceFromStored(childRow);
            const spouseNir = get(
              values,
              "spouse.identity.socialSecurityNumber",
            );
            const showPending = isPendingParentNir({
              authority,
              nirInheritanceFrom,
              adherentNir: adherentSocialSecurityNumber,
              spouseNir,
              hasSpouse,
            });
            const ssnReadOnly = !childNirIsManualEntry(
              authority,
              hasSpouse,
              nirInheritanceFrom,
            );
            const regimeMismatchMessage =
              get(formProps.errors, `children.${i}.identity.socialRegime`, "") ||
              get(formProps.errors, `children.${i}.authority`, "");
            const showRegimeMismatch =
              Boolean(regimeMismatchMessage) &&
              authority &&
              authority !== AUTHORITY_TYPE.SELF;

            return (
              <div
                key={`child_${i}`}
                className="miltis-related-persons__section"
              >
                <h3 className="miltis-related-persons__section-title">
                  Informations de votre {NUMBERS_MAPPING[i + 1]} enfant
                </h3>
                <div className="miltis-form">
                  <div className="miltis-form__container">
                    <Input
                      row
                      size="full"
                      label={"Civilité"}
                      type="radio"
                      values={[
                        {
                          value: GENDER.MALE,
                          label: "Homme",
                        },
                        {
                          value: GENDER.FEMALE,
                          label: "Femme",
                        },
                      ]}
                      {...bindInputProps({
                        name: `children.${i}.identity.user.gender`,
                        values,
                        ...formProps,
                      })}
                      onChange={(value) =>
                        setFieldValue(
                          `children.${i}.identity.user.gender`,
                          value,
                        )
                      }
                    />

                    <Input
                      row
                      size="full"
                      label={t("B2C_MILTIS.RELATED_PERSONS.AUTHORITY.LABEL")}
                      type="radio"
                      values={[
                        {
                          value: AUTHORITY_TYPE.FATHER,
                          label: t(
                            "B2C_MILTIS.RELATED_PERSONS.AUTHORITY.FATHER",
                          ),
                        },
                        {
                          value: AUTHORITY_TYPE.MOTHER,
                          label: t(
                            "B2C_MILTIS.RELATED_PERSONS.AUTHORITY.MOTHER",
                          ),
                        },
                        // {
                        //   value: AUTHORITY_TYPE.BOTH,
                        //   label: t(
                        //     "B2C_MILTIS.RELATED_PERSONS.AUTHORITY.BOTH",
                        //   ),
                        // },
                        {
                          value: AUTHORITY_TYPE.SELF,
                          label: t("B2C_MILTIS.RELATED_PERSONS.AUTHORITY.SELF"),
                        },
                      ]}
                      {...bindInputProps({
                        name: `children.${i}.authority`,
                        values,
                        ...formProps,
                      })}
                      onChange={(value) => {
                        setFieldValue(`children.${i}.authority`, value);
                        const baseFields =
                          get(values, `children.${i}.identity.fields`, {}) ||
                          {};
                        if (value !== AUTHORITY_TYPE.BOTH) {
                          const next = { ...baseFields };
                          delete next.nirInheritanceFrom;
                          setFieldValue(`children.${i}.identity.fields`, next);
                        } else {
                          setFieldValue(`children.${i}.identity.fields`, {
                            ...baseFields,
                            nirInheritanceFrom: NIR_INHERITANCE_FROM.ADHERENT,
                          });
                        }
                        if (
                          value === AUTHORITY_TYPE.SELF ||
                          childRequiresOffContractSpouseNir(
                            value,
                            hasSpouse,
                          )
                        ) {
                          setFieldValue(
                            `children.${i}.identity.socialSecurityNumber`,
                            "",
                          );
                        }
                      }}
                    />
                    {authority === AUTHORITY_TYPE.BOTH && (
                      <Select
                        options={[
                          {
                            value: NIR_INHERITANCE_FROM.ADHERENT,
                            label: t(
                              "B2C_MILTIS.RELATED_PERSONS.NIR_INHERITANCE_FROM.ADHERENT",
                            ),
                          },
                          {
                            value: NIR_INHERITANCE_FROM.SPOUSE,
                            label: t(
                              "B2C_MILTIS.RELATED_PERSONS.NIR_INHERITANCE_FROM.SPOUSE",
                            ),
                          },
                        ]}
                        placeholder={t(
                          "B2C_MILTIS.RELATED_PERSONS.NIR_INHERITANCE_FROM.LABEL",
                        )}
                        label={t(
                          "B2C_MILTIS.RELATED_PERSONS.NIR_INHERITANCE_FROM.LABEL",
                        )}
                        value={nirInheritanceFrom}
                        onChange={(value) => {
                          const baseFields =
                            get(values, `children.${i}.identity.fields`, {}) ||
                            {};
                          setFieldValue(`children.${i}.identity.fields`, {
                            ...baseFields,
                            nirInheritanceFrom: value,
                          });
                          if (
                            childRequiresOffContractSpouseNir(
                              AUTHORITY_TYPE.BOTH,
                              hasSpouse,
                              value,
                            )
                          ) {
                            setFieldValue(
                              `children.${i}.identity.socialSecurityNumber`,
                              "",
                            );
                          }
                        }}
                        errors={get(
                          formProps.errors,
                          `children.${i}.identity.fields.nirInheritanceFrom`,
                          "",
                        )}
                        touched={get(
                          formProps.touched,
                          `children.${i}.identity.fields.nirInheritanceFrom`,
                          false,
                        )}
                      />
                    )}

                    {showPending && (
                      <p
                        className="miltis-related-persons__alert-info"
                        role="status"
                      >
                        {t("B2C_MILTIS.RELATED_PERSONS.PARENT_NIR_PENDING", {
                          parent: pendingParentLabel(
                            authority,
                            nirInheritanceFrom,
                          ),
                        })}
                      </p>
                    )}
                    {showRegimeMismatch && (
                      <p
                        className="miltis-related-persons__alert-error"
                        role="alert"
                      >
                        {regimeMismatchMessage}
                      </p>
                    )}
                  </div>
                  <div className="miltis-form__row">
                    <Input
                      placeholder={"ex : Doe"}
                      label={"Nom"}
                      {...bindWebTextFieldInputProps({
                        name: `children.${i}.identity.user.lastname`,
                        values,
                        ...formProps,
                      })}
                    />

                    <Input
                      placeholder={"ex : John"}
                      label={"Prénom"}
                      {...bindWebTextFieldInputProps({
                        name: `children.${i}.identity.user.firstname`,
                        values,
                        ...formProps,
                      })}
                    />
                    <Input
                      placeholder={"ex : 185057800608436"}
                      label={"N° de Sécurité sociale"}
                      trim={true}
                      disabled={ssnReadOnly}
                      {...bindInputProps({
                        name: `children.${i}.identity.socialSecurityNumber`,
                        values,
                        ...formProps,
                      })}
                    />

                    <Select
                      options={Object.values(MILTIS_SOCIAL_REGIMES)
                        .filter(
                          (option) =>
                            !territorial ||
                            option !==
                              MILTIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS,
                        )
                        .map((option) => {
                          const key = Object.keys(MILTIS_SOCIAL_REGIMES).find(
                            (k) => MILTIS_SOCIAL_REGIMES[k] === option,
                          );
                          return {
                            value: option,
                            label: SOCIAL_REGIMES_TRANSLATION[key] || option,
                          };
                        })}
                      placeholder="Sélectionnez un régime"
                      label="Régime social"
                      value={get(values, `children.${i}.identity.socialRegime`)}
                      onChange={(value) =>
                        setFieldValue(
                          `children.${i}.identity.socialRegime`,
                          value,
                        )
                      }
                      errors={get(
                        formProps.errors,
                        `children.${i}.identity.socialRegime`,
                        "",
                      )}
                      touched={get(
                        formProps.touched,
                        `children.${i}.identity.socialRegime`,
                        false,
                      )}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          <ActionButtons
            disabled={!isEmpty(formProps.errors)}
            onNext={submitForm}
            onBack={onBack}
          />
          <DataProtection />
        </div>
      </div>
    </Layout>
  );
}

export default View;
